import React, { useRef, useState } from "react";
import {
    Backdrop,
    Divider,
    IconButton,
    InputAdornment,
    ListItemIcon,
    ListItemText,
    MenuItem,
    MenuList,
    Popper,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DotLegalDatePicker, commonInputStyles } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../localization/useTranslation";
import { AuditEntriesPreset } from "./AuditLogDialog.types";
import { StyledFilterPaper, TextFieldWrapper } from "../common/components/filterPopover/FilterPopover";
import { filterPopoverSubMenuWidth } from "../common/components/filterPopover/FilterPopoverSubMenu";

export interface PresetOption {
    id: AuditEntriesPreset;
    name: string;
}

export interface AuditDateRangeFilterProps {
    preset: AuditEntriesPreset | null;
    from: Date | null;
    to: Date | null;
    presetOptions: Array<PresetOption>;
    width?: string;
    onPresetChange: (preset: AuditEntriesPreset | null) => void;
    onFromChange: (from: Date | null) => void;
    onToChange: (to: Date | null) => void;
}

export function AuditDateRangeFilter(props: AuditDateRangeFilterProps) {
    const { translateString } = useTranslation();
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [draftFrom, setDraftFrom] = useState<Date | null>(props.from);
    const [draftTo, setDraftTo] = useState<Date | null>(props.to);
    const [draftPreset, setDraftPreset] = useState<AuditEntriesPreset | null>(props.preset);
    const anchorRef = useRef<HTMLDivElement>(null);

    const triggerValue = buildTriggerValue(props.preset, props.from, props.to, props.presetOptions, translateString("period"));

    function openPopover() {
        setDraftFrom(props.from);
        setDraftTo(props.to);
        setDraftPreset(props.preset);
        setOpen(true);
    }

    function onDraftFromChange(val: Date | null) {
        setDraftFrom(val);
        setDraftPreset(null);
    }

    function onDraftToChange(val: Date | null) {
        setDraftTo(val);
        setDraftPreset(null);
    }

    function commitAndClose() {
        if (!draftFrom && !draftTo) {
            props.onPresetChange("Past12Months");
        } else {
            if (draftFrom !== props.from) props.onFromChange(draftFrom);
            if (draftTo !== props.to) props.onToChange(draftTo);
        }
        setOpen(false);
    }

    return (
        <>
            <TextField
                ref={anchorRef}
                size="small"
                variant="outlined"
                value={triggerValue}
                onClick={() => (open ? commitAndClose() : openPopover())}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end" sx={{ marginRight: "-4px" }}>
                            <IconButton size="small" tabIndex={-1} sx={{ padding: "4px" }}>
                                <KeyboardArrowDownIcon />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{
                    width: props.width ?? "auto",
                    ...commonInputStyles(theme, "outlined", false, undefined),
                    "& .MuiOutlinedInput-root": {
                        cursor: "pointer",
                        paddingRight: "9px",
                    },
                    "& .MuiOutlinedInput-input": { cursor: "pointer" },
                }}
            />
            <Backdrop open={open} sx={{ zIndex: 1200 }} invisible onClick={commitAndClose} />
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                sx={{ minWidth: filterPopoverSubMenuWidth, zIndex: 1300 }}
                modifiers={[
                    { name: "offset", options: { offset: [0, 8] } },
                    { name: "flip", enabled: false },
                ]}
            >
                <StyledFilterPaper sx={{ maxHeight: "none", overflow: "visible", padding: 0, paddingRight: 0, marginTop: 0 }}>
                    <TextFieldWrapper>
                        <DotLegalDatePicker
                            label={translateString("from")}
                            value={draftFrom}
                            onChange={onDraftFromChange}
                            language={"en"}
                            noMargin
                            disablePortal
                        />
                        <DotLegalDatePicker label={translateString("to")} value={draftTo} onChange={onDraftToChange} language={"en"} noMargin disablePortal />
                    </TextFieldWrapper>
                    <Divider />
                    <MenuList dense sx={{ p: 0 }}>
                        {props.presetOptions.map((option) => {
                            const selected = draftPreset === option.id;
                            return (
                                <MenuItem
                                    key={option.id}
                                    selected={selected}
                                    onClick={() => {
                                        props.onPresetChange(selected ? null : option.id);
                                        setOpen(false);
                                    }}
                                    sx={{ fontSize: 14, px: "12px" }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 24,
                                            visibility: selected ? "visible" : "hidden",
                                        }}
                                    >
                                        <CheckIcon fontSize="small" sx={{ fontSize: 16 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" sx={{ fontSize: 14 }}>
                                                {option.name}
                                            </Typography>
                                        }
                                    />
                                </MenuItem>
                            );
                        })}
                    </MenuList>
                </StyledFilterPaper>
            </Popper>
        </>
    );
}

function buildTriggerValue(preset: AuditEntriesPreset | null, from: Date | null, to: Date | null, presetOptions: Array<PresetOption>, fallback: string) {
    if (preset) return presetOptions.find((o) => o.id === preset)?.name ?? fallback;
    if (from && to) return `${formatDate(from)} — ${formatDate(to)}`;
    if (from) return `${formatDate(from)} — …`;
    if (to) return `… — ${formatDate(to)}`;
    return fallback;
}

function formatDate(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}
